import { useState, useEffect } from "react";

import { AuthTransaction } from "pages/register/types/authTransaction";

import useGetTransaction from "./useGetTransaction";

import getAuthQueryParamData from "pages/register/util/getAuthQueryParamData";
import { getStateToken } from "pages/register/util/stateTokenManager";

// Hook to load an existing Okta transaction on page load
// 1. First attempts to load the transaction from the token passed as a query parameter in the URL
// 2. If the query token is invalid or does not exist, it attempts to load the transaction from the token saved in the client
const useTransactionLoader = () => {
  const queryToken = getAuthQueryParamData().transactionToken ?? "";
  const storageToken = getStateToken();

  const [queryTokenRequestFailed, setQueryTokenRequestFailed] =
    useState(!queryToken);
  const [fetchedTransaction, setFetchedTransaction] =
    useState<AuthTransaction>();

  const {
    data: responseFromQueryToken,
    error: errorFromQueryToken,
    isFetching: isFetchingWithQueryToken,
  } = useGetTransaction(queryToken, { enabled: !queryTokenRequestFailed });
  const {
    data: responseFromStorageToken,
    error: errorFromStorageToken,
    isFetching: isFetchingWithStorageToken,
  } = useGetTransaction(storageToken, { enabled: queryTokenRequestFailed });

  useEffect(() => {
    if (errorFromQueryToken) {
      setQueryTokenRequestFailed(true);
    }
  }, [errorFromQueryToken]);

  useEffect(() => {
    const transaction = responseFromQueryToken || responseFromStorageToken;

    if (transaction) {
      setFetchedTransaction(transaction);
    }
  }, [responseFromQueryToken, responseFromStorageToken]);

  return {
    transaction: fetchedTransaction,
    isLoading: isFetchingWithQueryToken || isFetchingWithStorageToken,
    error: errorFromStorageToken,
  };
};

export default useTransactionLoader;
